/**
 * ScrollToTop — resets the window scroll to (0, 0) on every route change.
 *
 * Why this is needed:
 *   React Router does NOT auto-scroll on navigation. Without this, after
 *   login the dashboard mounts with whatever scroll position the previous
 *   page (or the iOS URL-bar-collapse animation) left behind — clipping
 *   the "Welcome back, <name>" header behind the sticky mobile header.
 *
 *   Fix is global so every route (login → dashboard, sidebar nav, deep
 *   links from notifications, back-nav from a detail page) lands at the
 *   top consistently for every user on every device.
 *
 * Behaviour:
 *   - Uses useLayoutEffect so the scroll happens BEFORE the next paint —
 *     prevents the "flash" of clipped content visible to the user.
 *   - Resets every common scroll container (window, html, body) — covers
 *     iOS Safari quirks where window.scrollTo alone doesn't always take
 *     effect after a route change inside an installed PWA.
 *   - Schedules a follow-up scroll on the next animation frame to defeat
 *     the iOS Safari URL-bar-collapse race where the browser pushes the
 *     viewport down ~25px after the page has already rendered.
 *   - Skips when the URL contains a `#hash` so anchor links still work.
 */
import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

function resetScroll() {
  try {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  } catch (_) {
    window.scrollTo(0, 0);
  }
  if (document.documentElement) document.documentElement.scrollTop = 0;
  if (document.body) document.body.scrollTop = 0;
}

export default function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useLayoutEffect(() => {
    if (hash) return; // let the browser handle anchor links
    resetScroll();
    // iOS URL-bar-collapse fires AFTER the initial paint and re-pushes
    // the viewport down. Catch it on the next frame so the user never
    // sees the clipped header.
    const raf = requestAnimationFrame(resetScroll);
    // And once more after the iOS URL bar settles (~250ms) — belt and
    // braces; cheap, idempotent, no visible jump because we're already
    // at top.
    const t = setTimeout(resetScroll, 280);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [pathname, hash]);

  return null;
}
